import React, { useMemo } from "react";
import { Box, MenuItem, PaginationItem, Select, Typography } from "@mui/material";

const AlertPagination = ({
    paginationObj,
    onPageChange,
    localPage,
    rowsPerPage,
    totalRecords,
    onChange,
    onRowsPerPageChange,
    rowsPerPageOptions = [15, 25, 50, 100]
}) => {
    const isApiPagination = paginationObj || onPageChange;
    const pageSize = isApiPagination ? paginationObj?.per_page || rowsPerPage : rowsPerPage;

    const totalPages = useMemo(() => {
        if (isApiPagination) {
            return paginationObj?.total_pages || paginationObj?.totalPages || Math.ceil((paginationObj?.total || 0) / pageSize) || 1;
        }

        return Math.ceil(totalRecords / pageSize) || 1;
    }, [isApiPagination, paginationObj, pageSize, totalRecords]);

    const currentPaginationPage = isApiPagination ? paginationObj?.page || 1 : localPage + 1;

    const paginationPages = useMemo(() => {
        if (totalPages <= 8) {
            return Array.from({ length: totalPages }, (_, index) => index + 1);
        }

        const pages = new Set();
        const middlePage = Math.ceil(totalPages / 2);

        // Always show first 5 pages.
        for (let page = 1; page <= Math.min(5, totalPages); page += 1) {
            pages.add(page);
        }

        // Always show last 2 pages.
        pages.add(Math.max(1, totalPages - 1));
        pages.add(totalPages);

        // Always show one page before and after the current page.
        pages.add(Math.max(1, currentPaginationPage - 1));
        pages.add(currentPaginationPage);
        pages.add(Math.min(totalPages, currentPaginationPage + 1));

        // Always keep one middle page visible for quick jump.
        pages.add(middlePage);

        const sortedPages = [...pages]
            .filter((page) => page >= 1 && page <= totalPages)
            .sort((a, b) => a - b);

        const items = [];
        sortedPages.forEach((page, index) => {
            if (index === 0) {
                items.push(page);
                return;
            }

            const previousPage = sortedPages[index - 1];
            if (page - previousPage > 1) {
                items.push(`ellipsis-${previousPage}-${page}`);
            }

            items.push(page);
        });

        return items;
    }, [totalPages, currentPaginationPage]);

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                marginTop: "5px",
                flexWrap: "wrap"
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Typography sx={{ fontFamily: "Unilever Shilling", fontSize: "12px", color: "#111827" }}>
                    Rows per page
                </Typography>
                <Select
                    size="small"
                    value={pageSize}
                    onChange={(event) => onRowsPerPageChange(Number(event.target.value))}
                    sx={{
                        height: "28px",
                        fontFamily: "Unilever Shilling",
                        fontSize: "12px",
                        "& .MuiSelect-select": {
                            padding: "3px 28px 3px 8px"
                        }
                    }}
                >
                    {rowsPerPageOptions.map((option) => (
                        <MenuItem key={option} value={option} sx={{ fontFamily: "Unilever Shilling", fontSize: "12px" }}>
                            {option}
                        </MenuItem>
                    ))}
                </Select>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    "& .MuiPaginationItem-root": {
                        fontFamily: "Unilever Shilling",
                        fontSize: "12px",
                        height: "24px",
                        minWidth: "24px",
                        margin: "0 1px"
                    },
                    "& .MuiPaginationItem-page.Mui-selected": {
                        backgroundColor: "var(--primany-table-header-color)",
                        color: "#fff"
                    },
                }}
            >
                <PaginationItem
                    type="previous"
                    disabled={currentPaginationPage <= 1}
                    onClick={(event) => {
                        if (currentPaginationPage > 1) {
                            onChange(event, currentPaginationPage - 2, pageSize);
                        }
                    }}
                />

                {paginationPages.map((item) => {
                    if (typeof item === "string") {
                        return <PaginationItem key={item} type="start-ellipsis" disabled />;
                    }

                    return (
                        <PaginationItem
                            variant="outlined"
                            key={`page-${item}`}
                            page={item}
                            selected={item === currentPaginationPage}
                            onClick={(event) => onChange(event, item - 1, pageSize)}
                        />
                    );
                })}

                <PaginationItem
                    type="next"
                    disabled={currentPaginationPage >= totalPages}
                    onClick={(event) => {
                        if (currentPaginationPage < totalPages) {
                            onChange(event, currentPaginationPage, pageSize);
                        }
                    }}
                />
            </Box>
        </Box>
    );
};

export default AlertPagination;